import { useCallback, useState } from "react";
import { useUser } from "stores/useUser";
import { FirebaseAuth, FirestoreDB } from "utils/firebase";
import { deleteGoogleChannel, googleOAuthStart } from "api";
import { notify, isGoogleCalendarConnected, refreshUserFromFirestore } from "utils/common";

const useGoogleCalendar = () => {
  const { user, setUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isConnected = isGoogleCalendarConnected(user);

  const connect = async (returnTo = "/onboarding-calendar") => {
    setLoading(true);
    setError(null);

    try {
      const result = await googleOAuthStart(returnTo);
      window.location.assign(result.url);
      return { success: true, redirecting: true };
    } catch (err) {
      console.error("Calendar permission error:", err);
      const errorMessage = err.message || "Failed to connect calendar. Please try again.";
      setError(errorMessage);
      setLoading(false);
      notify(errorMessage, "error");
      return { success: false, error: errorMessage };
    }
  };

  const refreshConnection = useCallback(async () => {
    const userData = await refreshUserFromFirestore(FirebaseAuth, FirestoreDB);

    if (userData) {
      await Promise.resolve(setUser(userData));
    }

    return userData;
  }, [setUser]);

  const disconnect = async () => {
    setLoading(true);
    setError(null);

    try {
      await deleteGoogleChannel();

      // Update user store to remove credentials
      setUser({
        ...user,
        credentials: {
          ...user.credentials,
          accessToken: null,
          refreshToken: null,
        },
        channel: null,
        calendarConnected: false,
      });

      notify("Google Calendar disconnected successfully!", "success");
      return { success: true };
    } catch (err) {
      console.error("Error disconnecting Google Calendar:", err);
      const errorMessage = err.message || "Failed to disconnect Google Calendar";
      setError(errorMessage);
      notify(errorMessage, "error");
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    isConnected,
    connect,
    disconnect,
    loading,
    error,
    refreshConnection,
  };
};

export default useGoogleCalendar;
